import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';
import { SearchIcon } from './icons';

type FieldMeta = {
  label?: string;
  error?: string;
  hint?: string;
};

export type InputProps = InputHTMLAttributes<HTMLInputElement> & FieldMeta & {
  leadingIcon?: ReactNode;
  trailing?: ReactNode;
};

export function Input({
  label,
  error,
  hint,
  leadingIcon,
  trailing,
  className,
  id,
  ...props
}: InputProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const helpId = error || hint ? `${fieldId}-help` : undefined;

  return (
    <label className={['hopper-field', error ? 'has-error' : '', className].filter(Boolean).join(' ')} htmlFor={fieldId}>
      {label && <span className="hopper-field__label">{label}</span>}
      <span className="hopper-field__control">
        {leadingIcon && <span className="hopper-field__leading">{leadingIcon}</span>}
        <input {...props} id={fieldId} aria-invalid={Boolean(error) || undefined} aria-describedby={helpId} />
        {trailing && <span className="hopper-field__trailing">{trailing}</span>}
      </span>
      {(error || hint) && (
        <span className="hopper-field__help" id={helpId}>{error ?? hint}</span>
      )}
    </label>
  );
}

export type SearchInputProps = Omit<InputProps, 'type' | 'leadingIcon'>;

export function SearchInput(props: SearchInputProps) {
  return <Input {...props} type="search" leadingIcon={<SearchIcon size={18} />} />;
}

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & FieldMeta;

export function Select({ label, error, hint, className, id, children, ...props }: SelectProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const helpId = error || hint ? `${fieldId}-help` : undefined;

  return (
    <label className={['hopper-field', error ? 'has-error' : '', className].filter(Boolean).join(' ')} htmlFor={fieldId}>
      {label && <span className="hopper-field__label">{label}</span>}
      <span className="hopper-field__control hopper-field__control--select">
        <select {...props} id={fieldId} aria-invalid={Boolean(error) || undefined} aria-describedby={helpId}>
          {children}
        </select>
      </span>
      {(error || hint) && <span className="hopper-field__help" id={helpId}>{error ?? hint}</span>}
    </label>
  );
}
